import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Package, AlertTriangle, TrendingUp, TrendingDown, BarChart3, Warehouse} from 'lucide-react';
import {useInventory} from '../hooks/useInventory';
import {InventoryOverviewItem} from '../models/inventory.model';
import Button from '../../../Presentation/components/ui/Button';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import {AppTitle, AppText, AppCaption, AppCardTitle, AppBodyText, AppSmallText} from '../../../Presentation/components/ui/Typography';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {useDebounce} from '../../../Presentation/hooks/useDebounce';
import {InventoryOverviewSkeleton} from '../../../Presentation/components/ui/LoadingSkeleton';
import InventoryErrorBoundary from '../../../Presentation/components/ErrorHandling/InventoryErrorBoundary';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

import {useTablePagination} from '../../../Presentation/hooks/useTablePagination';
import {INVENTORY_ERROR_BOX_CLASS, inventoryEmptyText} from '../ui/inventoryUi';

// Helper function to get stock status color
const getStockStatusColor = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'IN_STOCK':
    case 'متوفر':
    case 'available':
      return 'bg-green-100 text-green-800';
    case 'LOW_STOCK':
    case 'مخزون منخفض':
    case 'low_stock':
      return 'bg-yellow-100 text-yellow-800';
    case 'OUT_OF_STOCK':
    case 'نفذ المخزون':
    case 'out_of_stock':
      return 'bg-red-100 text-red-800';
    case 'RESERVED':
    case 'محجوز':
    case 'reserved':
      return 'bg-brand-100 text-brand-800';
    case 'DAMAGED':
    case 'تالف':
    case 'damaged':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

interface InventoryOverviewPageProps {
  onAddStock?: (product: any) => void;
  productId?: string | number;
}

const InventoryOverviewPage: React.FC<InventoryOverviewPageProps> = () => {
  const {hasPermission} = useAuthorization();
  const {inventoryOverview, error, fetchInventoryOverview, overviewLoading, filters, updateFilters} = useInventory();

  const {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange
  } = useTablePagination({
    totalCount: inventoryOverview?.total_items || 0,
    onPageChange: (page, size) => {
      fetchInventoryOverview(filters, page, size);
    }
  });

  // Debounce filters to prevent excessive API calls
  const debouncedFilters = useDebounce(filters, 500);

  // Memoize the fetch function to prevent infinite loops
  const memoizedFetchOverview = useCallback(() => {
    fetchInventoryOverview(debouncedFilters, currentPage, pageSize);
  }, [debouncedFilters, currentPage, pageSize, fetchInventoryOverview]);

  useEffect(() => {
    memoizedFetchOverview();
  }, [memoizedFetchOverview]);

  // Memoized columns to prevent unnecessary re-renders
  const columns: TableColumn<InventoryOverviewItem>[] = useMemo(() => [
    {
      key: 'product_name',
      title: <AppTableHeaderCell label="المنتج" />,
      render: (product: InventoryOverviewItem) => (
        <div className="flex items-center gap-3">
          {product.product_image && (
            <img
              src={product.product_image}
              alt={product.product_name}
              className="w-10 h-10 rounded-lg object-cover"
              onError={(e) => {
                e.currentTarget.src = '/assets/images/store_placeholder.png';
              }}
            />
          )}
          <div>
            <AppText className="font-medium text-gray-900">{product.product_name}</AppText>
            <AppBodyText className="text-gray-500">{product.product_brand}</AppBodyText>
            <AppSmallText className="text-gray-400">{product.product_category}</AppSmallText>
          </div>
        </div>
      )
    },
    {
      key: 'product_barcode',
      title: <AppTableHeaderCell label="الباركود" />,
      render: (product: InventoryOverviewItem) => (
        <AppBodyText className="font-mono text-gray-600">{product.product_barcode}</AppBodyText>
      )
    },
    {
      key: 'total_quantity',
      title: <AppTableHeaderCell label="إجمالي الكمية" />,
      render: (product: InventoryOverviewItem) => (
        <div className="text-center">
          <AppTitle className="text-gray-900">{product.total_quantity}</AppTitle>
          <div className="flex justify-center gap-1 mt-1">
            <span className="bg-brand-100 text-brand-800 px-1 rounded">
              {product.warehouse_quantity} مستودع
            </span>
            <span className="bg-green-100 text-green-800 px-1 rounded">
              {product.marketers_quantity} مسوقين
            </span>
            {/* Stores removed - stores are customers, not inventory locations */}
          </div>
        </div>
      )
    },
    {
      key: 'total_locations',
      title: <AppTableHeaderCell label="عدد المواقع" />,
      render: (product: InventoryOverviewItem) => (
        <div className="text-center">
          <AppText className="font-medium text-gray-900">{product.total_locations}</AppText>
          <AppCaption className="text-gray-500">موقع</AppCaption>
        </div>
      )
    },
    {
      key: 'stock_status',
      title: <AppTableHeaderCell label="حالة المخزون" />,
      render: (product: InventoryOverviewItem) => (
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded font-medium ${getStockStatusColor(product.stock_status)}`}>
            {product.stock_status}
          </span>
          {product.has_serial && (
            <span className="bg-brand-100 text-brand-800 px-1 rounded">
              بسيريال
            </span>
          )}
        </div>
      )
    }
  ], [getStockStatusColor]);

  // Memoized summary data
  const summary = useMemo(() => {
    return {
      totalItems: inventoryOverview?.total_items || 0,
      totalQuantity: inventoryOverview?.total_quantity || 0,
      totalValue: inventoryOverview?.total_value || 0,
      lowStockCount: inventoryOverview?.low_stock_count || 0,
      outOfStockCount: inventoryOverview?.out_of_stock_count || 0
    };
  }, [inventoryOverview?.total_items, inventoryOverview?.total_quantity, inventoryOverview?.total_value, inventoryOverview?.low_stock_count, inventoryOverview?.out_of_stock_count]);

  // Check if user has permission to view inventory
  if (!hasPermission(PERMISSIONS.READ_INVENTORY) && !hasPermission(PERMISSIONS.MANAGE_INVENTORY)) {
    return (
      <div className="space-y-6">
        <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى نظرة عامة على المخزون. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
      </div>
    );
  }

  // Loading state with skeleton
  if (overviewLoading && !inventoryOverview) {
    return <InventoryOverviewSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className={INVENTORY_ERROR_BOX_CLASS}>
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">خطأ في تحميل البيانات</span>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <Button
            onClick={() => fetchInventoryOverview(filters, currentPage, pageSize)}
            className="mt-4"
            variant="outline"
          >
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }

  return (
    <InventoryErrorBoundary>
      <div className="space-y-6">
        {/* الإحصائيات العامة */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-brand-600" />
            <AppCardTitle>نظرة عامة على المخزون</AppCardTitle>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="text-center p-4 bg-brand-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Package className="w-6 h-6 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-brand-600">{summary?.totalItems || 0}</div>
              <div className="text-sm text-gray-600">إجمالي المنتجات</div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-green-600">
                {summary?.totalItems - summary?.lowStockCount - summary?.outOfStockCount || 0}
              </div>
              <div className="text-sm text-gray-600">منتجات متوفرة</div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="text-2xl font-bold text-yellow-600">
                {summary?.lowStockCount || 0}
              </div>
              <div className="text-sm text-gray-600">مخزون منخفض</div>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-red-600">
                {summary?.outOfStockCount || 0}
              </div>
              <div className="text-sm text-gray-600">نفذ المخزون</div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Warehouse className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {summary?.totalQuantity || 0}
              </div>
              <div className="text-gray-600">إجمالي الكمية</div>
            </div>

            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="text-2xl font-bold text-indigo-600">
                {summary?.totalQuantity / summary?.totalItems || 0}
              </div>
              <div className="text-gray-600">متوسط الكمية</div>
            </div>
          </div>
        </Card>

        {/* جدول المنتجات */}
        <Card className="p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-brand-500" />
              <AppCardTitle>تفاصيل المخزون</AppCardTitle>
              <span className="text-sm text-gray-500">
                عرض {inventoryOverview?.items?.length || 0} من أصل {inventoryOverview?.total_items || 0}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
          <AppTable
            columns={columns}
            data={inventoryOverview?.items || []}
            rowKey={(product, index) => `inventory-${product.product_id}-${index || 0}`}
            emptyText={inventoryEmptyText('منتجات في المخزون')}
            page={currentPage}
            pageSize={pageSize}
            totalCount={inventoryOverview?.total_items || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
          </div>
        </Card>
      </div>
    </InventoryErrorBoundary>
  );
};

export default React.memo(InventoryOverviewPage);
